import { Outlet, useLocation } from 'react-router-dom'
import Header from './Header/Header'
import Sidebar from './Sidebar'
import { useState, useEffect } from 'react'
import NavBar from './NavBar'
import { useBalance } from '@/hooks/useBalance'
import { useWalletStore } from '@/store/walletStore'
import WalletManager from '@/services/wallet/wallet-manager'
import { FloatingAd } from '../FloatingAd'

export const Layout = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()

  const { refreshBalance, refreshAllNetworksBalance } = useBalance()
  const { address, selectedNetwork, resetForWalletSwitch } = useWalletStore()

  // 1. Sincronización al cambiar de red, de wallet, o iniciar (Solo hace
  // loading visual si es cambio de red/wallet)
  useEffect(() => {
    if (!address || !selectedNetwork) return

    // Limpiar UI visualmente (balances, tokens, tx — no red/moneda, esos son globales)
    resetForWalletSwitch()

    const timer = setTimeout(() => {
      refreshBalance(true)
      // Cross-network balance se carga en paralelo, sin bloquear el loader de la red activa
      refreshAllNetworksBalance()
    }, 200)

    return () => clearTimeout(timer)
  }, [
    address,
    selectedNetwork,
    resetForWalletSwitch,
    refreshBalance,
    refreshAllNetworksBalance
  ])

  // 2. Sincronización en segundo plano cada 90 segundos (Silenciosa)
  useEffect(() => {
    if (!address) return

    const interval = setInterval(() => {
      refreshBalance(false)
      refreshAllNetworksBalance()
    }, 90000)

    return () => clearInterval(interval)
  }, [address, refreshBalance, refreshAllNetworksBalance])

  // 3. Detectar actividad del usuario para evitar auto-lock
  useEffect(() => {
    if (!address) return

    let throttleTimeout: NodeJS.Timeout | null = null
    const manager = WalletManager.getInstance()

    const handleActivity = () => {
      if (throttleTimeout) return

      // Extender la sesión en el manager
      manager.extendSession()

      // Throttle de 1 minuto para no saturar
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null
      }, 60000)
    }

    window.addEventListener('mousemove', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('click', handleActivity)

    return () => {
      window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('click', handleActivity)
      if (throttleTimeout) clearTimeout(throttleTimeout)
    }
  }, [address])

  // Pages where header should be hidden on desktop
  const hideHeaderOnDesktop = ['']
  const shouldHideHeader = hideHeaderOnDesktop.includes(location.pathname)

  // Pages where sidebar and navbar should be hidden
  const hideNavigation = ['/registration']
  const shouldHideNavigation = hideNavigation.includes(location.pathname)

  return (
    <div className="min-h-screen bg-app">
      {/* Sidebar - Desktop only */}
      {!shouldHideNavigation && (
        <div className="hidden lg:block">
          <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
        </div>
      )}

      {/* Header - Hidden on desktop for specific pages */}
      <div className={shouldHideHeader ? 'lg:hidden' : ''}>
        <Header hideSidebar={shouldHideNavigation} />
      </div>

      {/* NavBar - Mobile only */}
      {!shouldHideNavigation && (
        <div className="lg:hidden">
          <NavBar />
        </div>
      )}

      {/* Main Content */}
      <main
        className={`min-h-screen ${
          shouldHideNavigation
            ? 'pt-16 lg:pt-20'
            : shouldHideHeader
              ? 'pt-16 lg:ml-64 lg:pt-6'
              : 'pt-16 lg:ml-64 lg:pt-20'
        }`}
      >
        <div
          className={`${shouldHideNavigation ? 'p-6 lg:p-6 h-full' : 'p-6 lg:p-6 pb-32 lg:pb-6'}`}
        >
          <Outlet />
          <FloatingAd />
        </div>
      </main>
    </div>
  )
}
